import { UseQueryOptions, UseQueryResult } from '@tanstack/react-query';
import { useMemo } from 'react';
import { DefaultResponse } from '@vindr/services/interfaces';
import useMergedQuery from '@vindr/utils/hooks/useMergedQuery';

/**
 * @param defaultParams: based parameters for query data
 * @param queryFn: The query function
 * @param queryParams: Search by Backend parameters for query data
 * @param renderItem: render item
 * @param searchParams: Search by Frontend by initial list of query data
 */
export function FetchColumn<P, D>({
  defaultParams,
  queryFn,
  queryParams,
  renderItem,
  searchParams,
  options,
}: FetchColumnProps<P, D>) {
  const { data } = useMergedQuery<P, D>({
    queryFn,
    queryParams,
    searchParams,
    defaultParams,
    options,
  });

  const items = useMemo(() => {
    const result: string[] = [];
    data.forEach((item) => result.push(renderItem(item)));
    return result;
  }, [data, renderItem]);

  return <span title={items.join(', ')}>{items.join(', ')}</span>;
}

export interface FetchColumnCommonProps<P, D> {
  defaultParams: P;
  queryFn: (params: P, options?: UseQueryOptions) => UseQueryResult<DefaultResponse<D[]>>;
  renderItem: (data: D) => string;
  options?: UseQueryOptions;
}

export type FetchColumnRequireSearchParams<P, D> = FetchColumnCommonProps<P, D> & {
  searchParams: Partial<D>;
  queryParams?: P;
};

export type FetchColumnRequireQueryParams<P, D> = FetchColumnCommonProps<P, D> & {
  searchParams?: Partial<D>;
  queryParams: P;
};

export type FetchColumnProps<P, D> =
  | FetchColumnRequireSearchParams<P, D>
  | FetchColumnRequireQueryParams<P, D>;
